import { toast } from 'react-toastify';
import { Modal, ModalContent } from '@ds';
import { Form } from '@ui';
import { locationChanged } from '@lp/features/forms/CreateCaseForm/utils';
import { useUpdateCaseMutation, useUpdateWolsMutation } from '@/queries/case';
import { EditCaseDonorForm } from './EditCaseDonorForm';
import { CaseDonorFormType, caseDonorDefaultValues, caseDonorEditSchema } from './config';
import { EditCaseModalFooter } from '../components/EditCaseModalFooter';
import { getOPOFormContacts, mapAssignedOPOContacts, useUpdateOpoContacts } from '@/hooks/useUpdateOpoContacts';
import { retrieveUnifiedError } from '@/api/utils';
import { getChangedValues, getClinicalRecordByType, replaceEmptyWithNull } from '@/utils/helpers';
import { Case, ClinicalItemType, WOLSType } from '@/api/apis/case';

type EditGeneralCaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  caseData: Case;
};

export const EditDonorCaseModal = ({ isOpen, onClose, caseData }: EditGeneralCaseModalProps) => {
  const { status } = caseData;
  const updateOpoContacts = useUpdateOpoContacts();
  const { mutateAsync: updateCase } = useUpdateCaseMutation();
  const { mutateAsync: updateWols } = useUpdateWolsMutation();

  const handleSubmit = async (formData: CaseDonorFormType) => {
    const {
      pediatricCase,
      donorHospitalId,
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone,
      opoId,
      hospitalCustomLocation,
      wols,
      wolsComment,
    } = formData;
    const newFormValues = {
      pediatricCase,
      donorHospitalId,
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone,
      opoId,
      wols,
      wolsComment,
    };
    const { caseId, assignedOpoContacts } = caseData;
    const prevFormValues = caseDonorDefaultValues(caseData);

    try {
      const {
        wols: wolsChanged,
        wolsComment: wolsCommentChanged,
        ...valuesToPatch
      } = getChangedValues<Partial<CaseDonorFormType>>(newFormValues, prevFormValues);
      if (locationChanged(prevFormValues?.hospitalCustomLocation, hospitalCustomLocation)) {
        valuesToPatch.hospitalCustomLocation = hospitalCustomLocation;
      }
      if (Object.values(valuesToPatch).length) {
        await updateCase({ caseId, donorHospitalTimeZone, ...replaceEmptyWithNull(valuesToPatch) });
      }

      if ((wolsChanged || wolsCommentChanged) && wols) {
        const wolsText = wols === WOLSType.Other ? wolsComment : null;
        const wolsData = getClinicalRecordByType(caseData.clinicalData, ClinicalItemType.WithdrawalOfLifeSupport);
        await updateWols({
          id: wolsData?.item.id,
          caseId,
          wolsType: wols,
          text: wolsText || null,
        });
      }

      const existingContacts = mapAssignedOPOContacts(assignedOpoContacts).slice(0, 2);
      const newOpoContacts = getOPOFormContacts(
        formData.opoContactId,
        formData.secondaryOpoContactId,
        formData.opoSourceContactPhone,
        formData.secondaryOpoContactPhone,
      );
      await updateOpoContacts(existingContacts, newOpoContacts, caseId);

      toast.success('Case Donor data successfully saved.');
      onClose();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80',
        header: 'flex justify-between p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="4xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
    >
      <ModalContent>
        <Form<CaseDonorFormType>
          defaultValues={caseDonorDefaultValues(caseData)}
          schema={caseDonorEditSchema}
          onSubmit={handleSubmit}
        >
          <EditCaseDonorForm status={status} />
          <EditCaseModalFooter onClose={onClose} />
        </Form>
      </ModalContent>
    </Modal>
  );
};
